import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Moon, Sun, Phone } from "lucide-react";
import { BUSINESS } from "@/data/content";
import { useTheme } from "@/context/ThemeContext";
import { HOME } from "@/constants/testIds";
import Logo from "@/components/Logo";

const LINKS = [
  { label: "Home", to: "/#top" },
  { label: "Mobile Auto Detailing", to: "/#pricing" },
  { label: "Trash Bin Cleaning", to: "/bin-cleaning" },
  { label: "Authority+", to: "/#authority-plus" },
  { label: "Contact", to: "/#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const isDark = theme === "dark";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "py-2 backdrop-blur-xl bg-[#050914]/80 border-b border-white/10"
          : "py-4 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <Logo size={40} />
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-bold text-white text-sm sm:text-base tracking-tight">
              Authority Detailing Co.
            </div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-blue-400/80">
              Setting · Leading · Earning
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.to}
              className="text-sm text-white/70 hover:text-white transition-colors relative group"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-blue-500 transition-all group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            className="hidden md:inline-flex items-center gap-2 text-xs font-mono text-white/80 hover:text-white px-3 py-2 rounded-md border border-white/10 hover:border-blue-500/50 transition-colors"
            data-testid={HOME.clickToCall}
          >
            <Phone className="h-3.5 w-3.5" />
            {BUSINESS.phone}
          </a>
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            className="h-10 w-10 grid place-items-center rounded-md border border-white/10 text-white/80 hover:text-white hover:border-blue-500/40 transition-all"
            data-testid={HOME.themeToggle}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link
            to="/book"
            className="hidden sm:inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-all shadow-lg shadow-blue-600/30 hover:shadow-blue-500/50"
            data-testid={HOME.navBookNow}
          >
            Book Now
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-10 w-10 grid place-items-center rounded-md border border-white/10 text-white"
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-[#050914]/95 backdrop-blur-xl border-b border-white/10 px-5 py-4">
          <nav className="flex flex-col gap-3">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.to}
                className="text-white/80 hover:text-white text-base py-2 border-b border-white/5"
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/book"
              className="mt-2 bg-blue-600 text-white font-semibold py-3 rounded-md text-center"
              data-testid="mobile-nav-book-now"
            >
              Book Now
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
